/**
 * Полнота периода сравнения (Ф4 аудита 20.10, Б2). Бэкенд кладёт в ответы со сравнением `previousCoverage` — долю
 * дней периода сравнения, по которым источник загружен (0..1). Если загружено меньше 90 % дней, Δ к сравнению
 * ненадёжно (сравниваем с недогруженным прошлым): вместо Δ пишем «сравнение неполное» с подсказкой.
 * Агрегат полноты не меняет точки ряда: для этого нужна явная маска загруженных дней.
 *
 * Старый ответ без поля — полнота неизвестна; рассчитанные значения сохраняются с предупреждением.
 */

import { formatPercent, isNumber, toNumberOrNull } from './format'

export const COMPARE_COVERAGE_MIN = 0.9
export const INCOMPLETE_COMPARE_LABEL = 'сравнение неполное'

const pickCoverage = (raw: any): unknown => {
  if (!raw || typeof raw !== 'object') return undefined
  return raw.previousCoverage ?? raw.previous_coverage
}

/**
 * `previousCoverage` из ответа (или из нескольких мест по порядку — первое найденное). Доля 0..1; проценты
 * (1 < x ≤ 100) приводятся к доле. Нет поля — `null`.
 */
export function readCoverage(...sources: unknown[]): number | null {
  for (const source of sources) {
    const value = toNumberOrNull(pickCoverage(source))
    if (value === null || value < 0 || value > 100) continue
    return Math.min(1, value > 1 ? value / 100 : value)
  }
  return null
}

/** Сравнение неполное: поле есть и загружено меньше 90 % дней. */
export function isCompareIncomplete(coverage: number | null | undefined): boolean {
  return isNumber(coverage) && coverage < COMPARE_COVERAGE_MIN
}

export function incompleteCompareHint(coverage: number | null | undefined): string {
  const share = isNumber(coverage) ? `на ${formatPercent(coverage)} дней` : 'не полностью'
  return `Период сравнения загружен ${share} — изменение к нему не показываем, чтобы не сравнивать с недогруженными днями. Причина и догрузка — на странице «Качество данных»`
}

/**
 * Агрегат полноты не указывает, какие даты не загружены. Нули — реальные значения, null — разрывы;
 * сохраняем их позиции. Скрывать конкретный день можно только по явной маске источника.
 */
export function coveredCompareValues<T extends Array<number | null> | null | undefined>(
  values: T,
  _coverage: number | null | undefined,
  loaded?: readonly boolean[],
): T {
  if (!values || !loaded) return values
  return values.map((value, index) => (loaded[index] === false ? null : value)) as T
}

export const UNKNOWN_COMPARE_HINT =
  'Полнота периода сравнения неизвестна: источник не передал покрытие. Показанные изменения рассчитаны по полученным данным; пропуски могут влиять на результат.'
